import { useResizeObserver } from "@amzn/stencil-react-components/hooks";
import { Col, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { SearchResultV2 } from "common/components/SearchResultV2";
import { useLists } from "common/context/lists/ListsContext";
import { ONE_INDEX_BASED_RANK, SearchTelemetryMetadata } from "common/helpers/search-analytics";
import { CatalogItemSimplified } from "common/types";
import { CatalogItemSimplifiedWithRecommendations } from "common/types/catalogical-type";
import { PERMISSIONS } from "config";
import { useSearchExperience } from "hooks/useSearchExperience";
import { UserPermissions } from "lib/services";
import React, { FC, useEffect, useLayoutEffect } from "react";
import InfiniteScroll from "react-infinite-scroll-component";

export * from "./reducer";

export interface CatalogItemsListProps {
  /**
   * Function to call to verify if should load more items
   */
  shouldLoadMore: () => boolean;

  /**
   * Function to call on the need to searh more items
   */
  onSearchNext: () => void;

  /**
   * Id of the container to view
   */
  scrollableTarget: string;

  /**
   * List of items to show
   */
  items: CatalogItemSimplified[] | undefined;

  /**
   * Flag to indicate if the list is loading
   */
  isLoading: boolean;

  /**
   * Flag to indicate if the list is loading more items
   */
  isLoadingNextItems?: boolean;

  /**
   * Total count of items
   */
  totalCount: number | undefined;

  /**
   * Metadata to be sent to telemetry
   */
  searchMetadata?: SearchTelemetryMetadata;
}

const CatalogicalItemsList: FC<CatalogItemsListProps> = (props) => {
  // TODO: Remove the use of this hook since the data comes from the props:: https://taskei.amazon.dev/tasks/V2113918699
  const { items, totalCount, isLoading, searchCatalogItems } = useSearchExperience();
  const toggleLists = UserPermissions.current.hasPermission(PERMISSIONS.TOGGLE_LISTS);

  const { ref, height } = useResizeObserver<HTMLDivElement>();

  const hasMoreItems = () => {
    return items!.length < totalCount!;
  };

  const { yourLists, getYourLists } = useLists();
  useEffect(() => {
    if (toggleLists) {
      getYourLists({
        allowedDomains: ["software"],
      });
    }
  }, [toggleLists]);

  /* istanbul ignore next */
  useLayoutEffect(() => {
    if (!hasMoreItems()) return;
    if (props.shouldLoadMore()) {
      props.onSearchNext();
    }
  }, [height, isLoading]);

  /* istanbul ignore next */
  const itemsComponents = props.items!.map((item, index) => {
    const itemWithRec = item as CatalogItemSimplifiedWithRecommendations;
    const rank = index + ONE_INDEX_BASED_RANK;

    return (
      <SearchResultV2
        key={`${itemWithRec.itemId}`}
        taxonomyId={itemWithRec.itemId || ""}
        logoUrl={itemWithRec.logoImageUrl || ""}
        name={itemWithRec.name || ""}
        shortDescription={itemWithRec.shortDescription || ""}
        available={itemWithRec.status || ""}
        status={itemWithRec.status || ""}
        tags={[itemWithRec.itptClassification ?? ""]}
        price={itemWithRec.price}
        os={itemWithRec.os}
        eta={itemWithRec.etaDays}
        score={itemWithRec.score}
        recommendations={itemWithRec.recommendations}
        hasStock={itemWithRec.hasStock}
        lists={yourLists}
        averageRating={itemWithRec.feedback?.averageRating}
        numberOfRatings={itemWithRec.feedback?.numberOfRatings}
        ratingCounts={itemWithRec.feedback?.ratingCounts}
        rank={rank}
        searchMetadata={props.searchMetadata}
      />
    );
  });

  return (
    <Col ref={ref}>
      <InfiniteScroll
        dataLength={props.items?.length ?? 0}
        next={props.onSearchNext}
        hasMore={hasMoreItems()}
        loader={
          <View padding="S700">
            <Spinner size={SpinnerSize.Medium} />
          </View>
        }
        scrollableTarget={props.scrollableTarget}
      >
        {itemsComponents}
      </InfiniteScroll>
    </Col>
  );
};

export default CatalogicalItemsList;
